/**
 * ExecutionSummaryRenderer.
 *
 * Bespoke renderer for the `execution_summary` section of a tool-approval
 * card. The execution summary is the FIRST thing operators read when
 * reviewing an item — it answers "what happened?" — so it deserves
 * proper structure rather than the generic key-value flattening that
 * `type_aware` produces.
 *
 * Expected shape (all fields optional — the renderer degrades gracefully):
 *   {
 *     summary?: string,             // one-paragraph natural-language summary
 *     headline?: string,            // optional short headline
 *     automation_type?: string,     // classification (support_email, …)
 *     trigger?: string | object,    // what triggered execution
 *     customer?: {                  // customer block (any subset of fields)
 *       id?: string,
 *       email?: string,
 *       name?: string | null,
 *       customer_impact?: string,
 *       impact?: string,
 *       [k: string]: unknown,
 *     },
 *     customer_impact?: string,     // top-level customer impact (newer schema)
 *     investigation?: string[],     // chronological key findings
 *     key_actions?: string[],       // discrete actions WITH tool-call evidence
 *     unverified_claims?: string[], // actions the agent CLAIMED but did not execute
 *     risk_factors?: string[],      // operational concerns (errors, edge cases)
 *     outcome?: string,             // success | failed | pending_review | cancelled
 *     verdict?: string,             // final recommendation / outcome
 *     open_questions?: string[],    // unresolved items
 *     metadata?: Record<string, unknown>,
 *     [k: string]: unknown,         // catch-all
 *   }
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown, ChevronRight, FileText, Loader2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { useToast } from '@/shared/hooks'
import { generateExecutionSummary } from '../../../api'
import { useApprovalExecutionContext } from '../ApprovalExecutionContext'

export interface ExecutionSummaryRendererProps {
  data: unknown
}

/** Keys we render explicitly with bespoke styling. */
const RECOGNISED_KEYS = new Set([
  'summary',
  'headline',
  'automation_type',
  'trigger',
  'customer',
  'customer_impact',
  'investigation',
  'key_actions',
  'unverified_claims',
  'risk_factors',
  'outcome',
  'verdict',
  'open_questions',
  'metadata',
])

export function ExecutionSummaryRenderer({ data }: ExecutionSummaryRendererProps) {
  // Pull the execution id from the surrounding ApprovalCard context.
  // When present, we expose an on-demand "Summarise" / "Regenerate"
  // button so operators can trigger summarisation without having to
  // scroll through the full transcript. When absent (e.g. an attention
  // item not backed by an execution), the renderer behaves exactly
  // as before — read-only structured rendering.
  const { executionId } = useApprovalExecutionContext()
  const { toast } = useToast()

  // Locally-managed summary state. We start with whatever the backend
  // already sent down (via `data`); if the operator clicks "Generate",
  // we overlay the freshly-returned summary on top so the new content
  // renders inline without waiting for a detail refetch. This mirrors
  // the pattern used in `AttentionInspector.handleGenerateSummary`.
  const [generated, setGenerated] =
    React.useState<Record<string, unknown> | null>(null)
  const [isGenerating, setIsGenerating] = React.useState(false)

  // Effective summary that drives rendering. Generated state wins over
  // anything the section payload originally carried — the operator
  // just asked us to regenerate, so showing the older copy underneath
  // would be confusing.
  const effective: Record<string, unknown> | null = generated
    ?? (isPlainObject(data) ? (data as Record<string, unknown>) : null)

  // True when the data we have to render is meaningfully empty: either
  // no object at all, or an object with no fields the renderer knows
  // how to surface. We use this to decide between the empty-state CTA
  // and the structured rendering.
  const hasContent = effective !== null && hasRenderableContent(effective)

  const handleGenerate = React.useCallback(async () => {
    if (!executionId) return
    setIsGenerating(true)
    try {
      const result = await generateExecutionSummary(executionId)
      // Test renderable CONTENT, not object-ness. `{"summary": ""}` is a
      // non-null object, so the old `typeof … === 'object'` guard fired the
      // success toast for a payload this renderer then declined to draw —
      // producing the "success toast over an empty panel" contradiction in
      // eng_de4060dfdb76db96dba4ffd1. Reusing hasRenderableContent keeps the
      // toast and the body in agreement by construction.
      if (isPlainObject(result.execution_summary) && hasRenderableContent(result.execution_summary)) {
        setGenerated(result.execution_summary as Record<string, unknown>)
        toast({
          title: 'Summary generated',
          description: 'The execution summary is ready below.',
        })
      } else {
        toast({
          title: 'Summary generated, but empty',
          description:
            'The summariser ran but did not return any structured content. Try again, or open the execution to inspect the raw trace.',
        })
      }
    } catch (err) {
      toast({
        title: 'Failed to generate summary',
        description:
          err instanceof Error
            ? err.message
            : 'The summariser request did not complete. Please try again.',
      })
    } finally {
      setIsGenerating(false)
    }
  }, [executionId, toast])

  // Header bar with the action button. Only shown when we actually
  // have an execution id to point the summariser at — otherwise the
  // button is unusable and would just add noise.
  const actionBar = executionId ? (
    <div style={styles.actionBar}>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleGenerate}
        disabled={isGenerating}
        aria-label={hasContent ? 'Regenerate execution summary' : 'Summarise this conversation'}
        data-testid="generate-summary-button"
        style={styles.actionButton}
      >
        {isGenerating ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <FileText size={12} />
        )}
        {isGenerating
          ? 'Generating…'
          : hasContent
            ? 'Regenerate'
            : 'Summarise'}
      </Button>
    </div>
  ) : null

  // Degenerate inputs from the backend: bare string or non-object.
  // Preserve the existing behaviour (render the string as prose) and
  // still surface the action bar so operators can request a structured
  // summary on top of the legacy plain-text one.
  if (!isPlainObject(effective)) {
    if (typeof data === 'string' && data.trim().length > 0) {
      return (
        <div style={styles.container}>
          {actionBar}
          <p style={styles.summaryText}>{data}</p>
        </div>
      )
    }
    // Empty / null / non-object data without an execution id we can
    // act on — fall back to the historical "no summary" message so
    // we don't render an empty container.
    if (!executionId) {
      return <p style={styles.noData}>No execution summary provided.</p>
    }
    // We have an execution id but no content to show — render just
    // the CTA with a brief inline hint.
    return (
      <div style={styles.container}>
        {actionBar}
        <p style={styles.emptyHint} data-testid="execution-summary-empty-hint">
          No summary yet. Click <strong>Summarise</strong> to generate a
          structured overview of what the agent has done so far —
          quicker than reading the full transcript.
        </p>
      </div>
    )
  }

  // From here on `effective` is a plain object we'll render structurally.
  // If it has no fields the renderer recognises, treat it as empty.
  if (!hasContent) {
    // No executionId means no actionable affordance — fall back to the
    // historical "No execution summary provided." copy so we don't show
    // hint text that promises a button that isn't there.
    if (!executionId) {
      return <p style={styles.noData}>No execution summary provided.</p>
    }
    return (
      <div style={styles.container}>
        {actionBar}
        <p style={styles.emptyHint} data-testid="execution-summary-empty-hint">
          No summary yet. Click <strong>Summarise</strong> to generate a
          structured overview of what the agent has done so far —
          quicker than reading the full transcript.
        </p>
      </div>
    )
  }

  const obj = effective
  const headline = stringOrNull(obj.headline)
  const summary = stringOrNull(obj.summary)
  const verdict = stringOrNull(obj.verdict)
  // NOTE: `outcome` is intentionally not read here. It's surfaced as a
  // status chip in the card header (see ApprovalCard.extractOutcome)
  // rather than as a body sub-block, so reading it from `obj` would
  // be dead code.
  const automationType = stringOrNull(obj.automation_type)
  const trigger = obj.trigger
  const customer = isPlainObject(obj.customer) ? (obj.customer as Record<string, unknown>) : null
  // customer_impact may live at the top level (newer summariser schema) or
  // nested under customer.* (older shape). Prefer top-level if present.
  const topLevelCustomerImpact = stringOrNull(obj.customer_impact)
  const investigation = stringArray(obj.investigation)
  const keyActions = stringArray(obj.key_actions)
  const unverifiedClaims = stringArray(obj.unverified_claims)
  const riskFactors = stringArray(obj.risk_factors)
  const openQuestions = stringArray(obj.open_questions)
  const metadata = isPlainObject(obj.metadata) ? (obj.metadata as Record<string, unknown>) : null

  // Catch-all for any unknown extra fields the backend adds later.
  const extras: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (RECOGNISED_KEYS.has(k)) continue
    if (v === null || v === undefined || v === '') continue
    extras[k] = v
  }

  return (
    <div style={styles.container}>
      {actionBar}

      {headline && <h4 style={styles.headline}>{headline}</h4>}

      {summary && <p style={styles.summaryText}>{summary}</p>}

      {unverifiedClaims.length > 0 && (
        <WarningBlock label="Unverified Claims">
          <p style={styles.warningPreamble}>
            The underlying agent stated the following action(s) were taken, but
            no corresponding tool call or step execution was found in the trace.
            Operator review required.
          </p>
          <ul style={styles.bulletList}>
            {unverifiedClaims.map((claim, idx) => (
              <li key={idx} style={styles.bulletItem}>{claim}</li>
            ))}
          </ul>
        </WarningBlock>
      )}

      {verdict && (
        <SubBlock label="Verdict">
          <p style={styles.summaryText}>{verdict}</p>
        </SubBlock>
      )}

      {/*
       * NOTE: outcome is intentionally NOT rendered as its own SubBlock
       * here. It's lifted up into the ApprovalCardHeader as a status
       * chip ("Pending review", "Success", …) so the headline state
       * lands where the eye first goes. Repeating it in the body
       * produced a one-word sub-section that wasted vertical space.
       */}

      {keyActions.length > 0 && (
        <SubBlock label="Key Actions">
          <ul style={styles.bulletList}>
            {keyActions.map((action, idx) => (
              <li key={idx} style={styles.bulletItem}>{action}</li>
            ))}
          </ul>
        </SubBlock>
      )}

      {riskFactors.length > 0 && (
        <SubBlock label="Risk Factors">
          <ul style={styles.bulletList}>
            {riskFactors.map((rf, idx) => (
              <li key={idx} style={styles.bulletItem}>{rf}</li>
            ))}
          </ul>
        </SubBlock>
      )}

      {investigation.length > 0 && (
        <SubBlock label="Investigation">
          <ul style={styles.bulletList}>
            {investigation.map((step, idx) => (
              <li key={idx} style={styles.bulletItem}>{step}</li>
            ))}
          </ul>
        </SubBlock>
      )}

      {openQuestions.length > 0 && (
        <SubBlock label="Open Questions">
          <ul style={styles.bulletList}>
            {openQuestions.map((q, idx) => (
              <li key={idx} style={styles.bulletItem}>{q}</li>
            ))}
          </ul>
        </SubBlock>
      )}

      {topLevelCustomerImpact && !customer && (
        <SubBlock label="Customer Impact">
          <p style={styles.summaryText}>{topLevelCustomerImpact}</p>
        </SubBlock>
      )}

      {customer && (
        <CustomerBlock
          customer={customer}
          fallbackImpact={topLevelCustomerImpact}
        />
      )}

      {(automationType || trigger !== undefined) && (
        <CollapsibleSubBlock label="Context" defaultCollapsed>
          <dl style={styles.metaList}>
            {automationType && (
              <MetaRow label="Automation type" value={humanise(automationType)} />
            )}
            {typeof trigger === 'string' && trigger.trim().length > 0 && (
              <MetaRow label="Trigger" value={trigger} />
            )}
            {isPlainObject(trigger) && (
              <MetaRow label="Trigger" value={compactObject(trigger as Record<string, unknown>)} />
            )}
          </dl>
        </CollapsibleSubBlock>
      )}

      {Object.keys(extras).length > 0 && (
        <CollapsibleSubBlock label="Other" defaultCollapsed>
          <dl style={styles.metaList}>
            {Object.entries(extras).map(([k, v]) => (
              <MetaRow key={k} label={humanise(k)} value={renderUnknown(v)} />
            ))}
          </dl>
        </CollapsibleSubBlock>
      )}

      {metadata && Object.keys(metadata).length > 0 && (
        <CollapsibleSubBlock label="Metadata" defaultCollapsed muted>
          <dl style={styles.metaList}>
            {Object.entries(metadata).map(([k, v]) => (
              <MetaRow key={k} label={humanise(k)} value={renderUnknown(v)} muted />
            ))}
          </dl>
        </CollapsibleSubBlock>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SubBlock({
  label,
  children,
  muted,
}: {
  label: string
  children: React.ReactNode
  muted?: boolean
}) {
  return (
    <section style={styles.subBlock}>
      <h5 style={{ ...styles.subBlockLabel, ...(muted ? styles.subBlockLabelMuted : {}) }}>
        {label}
      </h5>
      {children}
    </section>
  )
}

/**
 * Prominent warning-styled block. Used for `unverified_claims` — the case
 * where the underlying agent told the customer (or wrote in its response)
 * that an action had been taken, but no tool call backs it up. Operators
 * MUST see this immediately, so it gets a coloured border and is rendered
 * directly under the summary rather than further down with the rest of the
 * sub-blocks.
 */
function WarningBlock({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <section style={styles.warningBlock} role="alert">
      <h5 style={styles.warningLabel}>{label}</h5>
      {children}
    </section>
  )
}

/**
 * Collapsible variant of SubBlock used for secondary information
 * (Context, Other, Metadata) that the operator may want hidden by
 * default to reduce visual noise in the execution summary.
 */
function CollapsibleSubBlock({
  label,
  children,
  defaultCollapsed = false,
  muted,
}: {
  label: string
  children: React.ReactNode
  defaultCollapsed?: boolean
  muted?: boolean
}) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed)
  return (
    <section style={styles.subBlock}>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        style={styles.collapsibleHeader}
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight style={styles.collapsibleChevron} aria-hidden="true" />
        ) : (
          <ChevronDown style={styles.collapsibleChevron} aria-hidden="true" />
        )}
        <h5 style={{ ...styles.subBlockLabel, ...(muted ? styles.subBlockLabelMuted : {}) }}>
          {label}
        </h5>
      </button>
      {!collapsed && <div style={styles.collapsibleBody}>{children}</div>}
    </section>
  )
}

function CustomerBlock({
  customer,
  fallbackImpact,
}: {
  customer: Record<string, unknown>
  fallbackImpact?: string | null
}) {
  const name = stringOrNull(customer.name)
  const email = stringOrNull(customer.email)
  const id = stringOrNull(customer.id)
  const impact =
    stringOrNull(customer.customer_impact) ??
    stringOrNull(customer.impact) ??
    (fallbackImpact ?? null)

  // Anything else under customer.* renders as a generic row.
  const otherEntries = Object.entries(customer).filter(
    ([k, v]) =>
      !['name', 'email', 'id', 'customer_impact', 'impact'].includes(k) &&
      v !== null && v !== undefined && v !== '',
  )

  const hasIdentity = name || email || id
  if (!hasIdentity && !impact && otherEntries.length === 0) return null

  return (
    <SubBlock label="Customer">
      {hasIdentity && (
        <div style={styles.customerIdentity}>
          {name && <span style={styles.customerName}>{name}</span>}
          {email && <span style={styles.customerEmail}>{email}</span>}
          {id && !name && !email && <span style={styles.customerEmail}>{id}</span>}
        </div>
      )}
      {impact && <p style={styles.summaryText}>{impact}</p>}
      {otherEntries.length > 0 && (
        <dl style={styles.metaList}>
          {otherEntries.map(([k, v]) => (
            <MetaRow key={k} label={humanise(k)} value={renderUnknown(v)} />
          ))}
        </dl>
      )}
    </SubBlock>
  )
}

function MetaRow({
  label,
  value,
  muted,
}: {
  label: string
  value: React.ReactNode
  muted?: boolean
}) {
  return (
    <div style={styles.metaRow}>
      <dt style={{ ...styles.metaLabel, ...(muted ? styles.metaLabelMuted : {}) }}>{label}</dt>
      <dd style={styles.metaValue}>{value}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Returns true if the object has any field the renderer can meaningfully
 * surface. Used to distinguish "the backend handed us an empty placeholder
 * because no summary exists yet" from "the backend handed us a populated
 * summary we should render". Without this check, an empty `{}` would
 * produce an empty container with no visual feedback, leaving operators
 * confused about whether the section was working.
 *
 * Exported because it is the single definition of "empty" for the summary
 * surfaces: this renderer, `AttentionInspector`'s ExecutionSummarySection,
 * and both of their post-generate guards. The backend mirrors it in
 * `execution_summariser.go:hasRenderableContent`. Having two definitions
 * one layer apart is what produced eng_de4060dfdb76db96dba4ffd1 — do not
 * re-implement it locally.
 */
export function hasRenderableContent(obj: Record<string, unknown>): boolean {
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue
    if (typeof v === 'string' && v.trim().length === 0) continue
    if (Array.isArray(v) && v.length === 0) continue
    if (isPlainObject(v) && Object.keys(v).length === 0) continue
    // `outcome` alone is surfaced as a header chip rather than in the body,
    // so a payload containing only outcome shouldn't qualify as "renderable".
    if (k === 'outcome') continue
    return true
  }
  return false
}

function stringOrNull(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const trimmed = v.trim()
  return trimmed.length > 0 ? trimmed : null
}

function stringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim()
      if (isPlainObject(entry)) {
        // Common pattern: { step, detail } or { action, reason }
        const text = stringOrNull(entry.text) ?? stringOrNull(entry.summary) ?? stringOrNull(entry.action) ?? stringOrNull(entry.step)
        if (text) return text
        return JSON.stringify(entry)
      }
      return entry == null ? '' : String(entry)
    })
    .filter((s) => s.length > 0)
}

function humanise(key: string): string {
  return key
    .replace(/[_\-.]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function compactObject(obj: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      parts.push(`${humanise(k)}: ${v}`)
    }
  }
  if (parts.length === 0) {
    try { return JSON.stringify(obj) } catch { return String(obj) }
  }
  return parts.join(' · ')
}

function renderUnknown(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) {
    return (
      <ul style={styles.bulletList}>
        {v.map((entry, idx) => (
          <li key={idx} style={styles.bulletItem}>{renderUnknown(entry)}</li>
        ))}
      </ul>
    )
  }
  if (isPlainObject(v)) {
    return compactObject(v)
  }
  return String(v)
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  noData: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  // Action bar above the summary body, hosting the
  // "Summarise" / "Regenerate" button. Right-aligned so it doesn't
  // compete visually with the section heading rendered above by
  // `ApprovalCardSection`.
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  // Hint copy shown next to the CTA when there's no summary yet. The
  // sentence-case prose deliberately calls out what the button does
  // and why operators would want to use it — "click Summarise to skip
  // the transcript" — because the section is the first thing they
  // see on opening an approval and we want the affordance to be
  // legible at a glance.
  emptyHint: {
    fontSize: '14px',
    lineHeight: 1.55,
    color: 'var(--text-muted)',
    margin: 0,
  },
  headline: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  },
  summaryText: {
    fontSize: '14px',
    lineHeight: 1.55,
    color: 'var(--text-primary)',
    margin: 0,
  },
  subBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  // Sub-block labels (Verdict, Key Actions, Customer Impact, …) sit
  // INSIDE a top-level collapsible section, so they must read as
  // clearly subordinate to that section's heading. The 11px ALL-CAPS
  // treatment paired with the larger sentence-case section heading
  // creates a two-tier visual hierarchy.
  subBlockLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted)',
    margin: 0,
  },
  subBlockLabelMuted: {
    opacity: 0.7,
  },
  warningBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px 12px',
    borderLeft: '3px solid var(--color-warning, #c8801a)',
    background: 'var(--color-warning-bg, rgba(200, 128, 26, 0.08))',
    borderRadius: '4px',
  },
  warningLabel: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-warning, #c8801a)',
    margin: 0,
  },
  warningPreamble: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--text-primary)',
    margin: 0,
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
  },
  collapsibleChevron: {
    width: '12px',
    height: '12px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  collapsibleBody: {
    paddingLeft: '18px',
  },
  // Bullet list used for Key Actions, Investigation, Risk Factors,
  // Open Questions. Tightened paddingLeft + gap so the items read as
  // a definition-style checklist rather than a loose paragraph stack.
  bulletList: {
    margin: 0,
    paddingLeft: '18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  bulletItem: {
    fontSize: '14px',
    lineHeight: 1.45,
    color: 'var(--text-primary)',
  },
  customerIdentity: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    flexWrap: 'wrap',
  },
  customerName: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  customerEmail: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    fontFamily: "'JetBrains Mono', monospace",
  },
  metaList: {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  metaRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, max-content) 1fr',
    gap: '12px',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  metaLabel: {
    margin: 0,
    color: 'var(--text-muted)',
  },
  metaLabelMuted: {
    opacity: 0.7,
  },
  metaValue: {
    margin: 0,
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
  },
}

export default ExecutionSummaryRenderer
